import type { IWorkspace } from '../api/src/workspace';
import type { IQueueItem } from '../api/src/queue';
import type { IDecision, IExtraction } from '../api/src/filing/decide';
import { getToken } from './auth';
import { AI_API_BASE, API_SCOPES } from './config';
import { apiTimeoutMs, apiUrl as routeFor } from './apiUrl';
import { PRODUCT_NAME } from './app/product';

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly left?: number, public readonly upgrade?: boolean) {
    super(message);
  }
}

/** Calls our backend with the signed-in user's token (Static Web Apps reserves Authorization). */
export async function callApi<T>(path: string, init?: RequestInit, interactive: boolean = true): Promise<T> {
  const token: string = await getToken(API_SCOPES, interactive);
  // Never wait forever: a call that doesn't answer in time is an error the person can act on.
  const controller: AbortController = new AbortController();
  const timer = setTimeout(() => controller.abort(), apiTimeoutMs(path));
  let response: Response;
  try {
    response = await fetch(routeFor(path, AI_API_BASE), {
      ...init,
      signal: init?.signal ?? controller.signal,
      headers: { ...(init?.headers ?? {}), 'x-organizer-token': token, 'Content-Type': 'application/json' }
    });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new ApiError(504, `${PRODUCT_NAME}'s service took too long to answer. Try again in a moment.`);
    }
    throw new ApiError(0, `${PRODUCT_NAME}'s service couldn't be reached. Check your connection and try again.`);
  } finally {
    clearTimeout(timer);
  }
  const body = (await response.json().catch(() => ({}))) as { error?: string; left?: number; upgrade?: boolean } & T;
  if (!response.ok) {
    throw new ApiError(response.status, body.error ?? `Something went wrong on our side (${response.status}). Try again in a moment.`, body.left, body.upgrade);
  }
  return body;
}

export type Plan = 'trial' | 'paid';

export interface ICredits {
  balance: number;
  provider: string;
  dataResidency?: 'global' | 'us' | 'canada';
  plan?: Plan;
  /** Free trial: what's left of its yearly allowance, and when it renews. */
  trial?: ITrialAllowance;
}

export interface ITrialAllowance {
  applications: number;
  filing: number;
  renews: string;
}

/** Counts an application opened (counts only). Refused with 402 when the free trial's applications are used. */
export function applicationOpened(): Promise<{ ok: boolean }> {
  return callApi('applications/opened', { method: 'POST', body: JSON.stringify({}) });
}

export interface IUploadLinkInfo {
  nonce: string;
  createdBy: string;
  createdAt: string;
  expires: string;
  revoked: boolean;
  uploads: number;
}

/** A link a client uses to upload documents for one application, into its "From client" folder. */
export function createUploadLink(driveId: string, applicationKey: string, folderId: string, restricted: boolean, days: number = 30): Promise<{ url: string; nonce: string; expires: string }> {
  return callApi('uploads/link', { method: 'POST', body: JSON.stringify({ driveId, applicationKey, folderId, days, restricted }) });
}

export function listUploadLinks(applicationKey: string): Promise<{ links: IUploadLinkInfo[] }> {
  return callApi(`uploads/links/${encodeURIComponent(applicationKey)}`);
}

export function revokeUploadLink(nonce: string): Promise<{ ok: boolean }> {
  return callApi('uploads/revoke', { method: 'POST', body: JSON.stringify({ nonce }) });
}

export function fetchCredits(): Promise<ICredits> {
  return callApi('credits', undefined, false);
}

export interface IWorkspaceSummary {
  driveId: string;
  siteId: string;
  siteName: string;
  libraryName: string;
  webUrl: string;
  setupDone: boolean;
}

export async function listWorkspaces(): Promise<IWorkspaceSummary[]> {
  return (await callApi<{ workspaces: IWorkspaceSummary[] }>('workspaces')).workspaces;
}

export async function getWorkspace(driveId: string): Promise<IWorkspace | undefined> {
  try {
    return await callApi<IWorkspace>(`workspace/${encodeURIComponent(driveId)}`);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return undefined;
    }
    throw error;
  }
}

/** Saves the whole settings object; refused (409) if someone else saved since it was read. */
export function saveWorkspace(w: IWorkspace): Promise<IWorkspace> {
  return callApi<IWorkspace>(`workspace/${encodeURIComponent(w.driveId)}`, { method: 'PUT', body: JSON.stringify(w) });
}

export async function getQueue(driveId: string): Promise<IQueueItem[]> {
  return (await callApi<{ items: IQueueItem[] }>(`queue/${encodeURIComponent(driveId)}`)).items;
}

export async function putQueue(driveId: string, items: IQueueItem[]): Promise<void> {
  for (let i = 0; i < items.length; i += 50) {
    await callApi(`queue/${encodeURIComponent(driveId)}`, { method: 'PUT', body: JSON.stringify({ items: items.slice(i, i + 50) }) });
  }
}

export async function removeFromQueue(driveId: string, itemIds: string[]): Promise<void> {
  for (let i = 0; i < itemIds.length; i += 50) {
    await callApi(`queue/${encodeURIComponent(driveId)}/remove`, { method: 'POST', body: JSON.stringify({ itemIds: itemIds.slice(i, i + 50) }) });
  }
}

export interface IReadFile {
  id: string;
  name: string;
  extension: string;
  folderPath: string;
  downloadUrl?: string;
}

export interface IReadResult {
  id: string;
  extraction: IExtraction;
  decision: IDecision;
  learnedUsed: string[];
  excerptStatus?: string;
}

/** Reads documents for filing (one document each from the plan; refunded if nothing could be read). */
export async function readForFiling(driveId: string, files: IReadFile[]): Promise<{ results: IReadResult[]; balance: number }> {
  return callApi('filing/read', { method: 'POST', body: JSON.stringify({ driveId, files }) });
}

/** Forgets one library in AI Admin (settings, queue, saved design, status). Nothing in SharePoint changes. */
export async function forgetLibrary(driveId: string): Promise<void> {
  await callApi(`workspace/${encodeURIComponent(driveId)}`, { method: 'DELETE' });
}

/** Forgets everything AI Admin keeps about the organisation's libraries (billing records stay). */
export async function deleteOrganisationData(): Promise<void> {
  await callApi('organisation-data', { method: 'DELETE', body: JSON.stringify({ confirm: 'delete-everything' }) });
}
